import React, { useEffect, useState, useMemo, useContext } from 'react';
import DatePicker from '../../components/dateComponents/DatePicker2';
import CustomChart from '../../components/chartComponents/customChart';
import { dbPost } from '../../apis/commonApi';
import { formatDateToISOString } from '../../services/UtilService';
import { ModalContext } from '../../components/contexts';

const ResScoreStats = () => {
  useEffect(() => {
    document.title = '통계 - AI 영상 분석 점수';
  }, []);
  const [chartData, setChartData] = useState([]);
  const [filter, setFilter] = useState({
    startDate: null,
    endDate: null,
    scoreType: 'OWAS',
  });
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [aiEntNmList, setAiEntNmList] = useState([]);
  const [aiTaskNmList, setAiTaskNmList] = useState([]);
  const [aiTpbizNmList, setAiTpbizNmList] = useState([]);
  const [selectedEntNm, setSelectedEntNm] = useState('');
  const [selectedTaskNm, setSelectedTaskNm] = useState('');
  const [selectedTpbizNm, setSelectedTpbizNm] = useState('');
  const [selectedScoreType, setSelectedScoreType] = useState('OWAS');
  const [filteredTaskList, setFilteredTaskList] = useState([]);
  const [selectedApplySe, setSelectedApplySe] = useState('');

  const fetchChartData = async (params = {}) => {
    try {
      const response = await dbPost('/stats/resScoreStats/search.do', params);
      setChartData(response.data || []);
      const categories = response.map(item => item.regDt);
      const series = [
        { name: '매우 낮음', data: [] },
        { name: '낮음', data: [] },
        { name: '보통', data: [] },
        { name: '높음', data: [] },
        { name: '매우 높음', data: [] },
      ];
      response.forEach(item => {
        const total =
          item.score1 + item.score2 + item.score3 + item.score4 + item.score5 ||
          1;
        series[0].data.push(
          parseFloat(((item.score1 / total) * 100).toFixed(2))
        ); // 매우 낮음
        series[1].data.push(
          parseFloat(((item.score2 / total) * 100).toFixed(2))
        ); // 낮음
        series[2].data.push(
          parseFloat(((item.score3 / total) * 100).toFixed(2))
        ); // 보통
        series[3].data.push(
          parseFloat(((item.score4 / total) * 100).toFixed(2))
        ); // 높음
        series[4].data.push(
          parseFloat(((item.score5 / total) * 100).toFixed(2))
        ); // 매우 높음
      });
      setChartData({ categories, series });
    } catch (error) {}
  };

  const fetchSearchData = async () => {
    try {
      const response = await dbPost('/stats/aiStats/search.do');
      if (response) {
        setAiEntNmList(response.aiEntNmList || []);
        setAiTaskNmList(response.aiTaskNmList || []);
        setAiTpbizNmList(response.aiTpbizNmList || []);
      }
    } catch (error) {}
  };

  useEffect(() => {
    fetchSearchData();
  }, []);

  useEffect(() => {
    fetchChartData(filter);
  }, [filter]);

  const handleFilterChange = updatedFilter => {
    setFilter(prev => ({ ...prev, ...updatedFilter }));
  };

  const handleSearch = () => {
    const updatedFilter = {
      startDate: formatDateToISOString(customStartDate) || filter.startDate,
      endDate: formatDateToISOString(customEndDate, true) || filter.endDate,
      entNm: selectedEntNm,
      taskNm: selectedTaskNm,
      tpbizNm: selectedTpbizNm,
      scoreType: selectedScoreType || 'OWAS',
      applySe: selectedApplySe,
    };
    setFilter(updatedFilter);
  };

  const handleScoreTypeChange = e => {
    const value = e.target.value || 'OWAS';
    setSelectedScoreType(value);
  };

  const handleTpbizChange = e => {
    const selectedTpbiz = e.target.value;
    setSelectedTpbizNm(selectedTpbiz);

    if (selectedTpbiz) {
      const tpbiz = aiTpbizNmList.find(item => item.tpbizNm === selectedTpbiz);
      if (tpbiz) {
        const filteredTasks = aiTaskNmList.filter(
          task => task.tpbizTypeId === tpbiz.tpbizTypeId
        );
        setFilteredTaskList(filteredTasks);
      } else {
        setFilteredTaskList([]);
      }
    } else {
      setFilteredTaskList([]);
    }

    setSelectedTaskNm('');
  };

  const chartOptions = useMemo(
    () => ({
      chart: {
        width: '100%',
        height: '500px',
      },
      xAxis: {
        title: '날짜',
        type: 'category',
      },
      yAxis: {
        title: '%',
        min: 0,
        max: 100,
      },
      series: {
        stack: true,
      },
      legend: {
        align: 'bottom',
      },
      tooltip: {
        grouped: true,
      },
      theme: {
        series: {
          colors: ['#28a745', '#fff700', '#ffa600', '#ff4200', '#dc3545'], // 초록, 노랑, 주황, 살짝 붉은색, 붉은색
        },
      },
    }),
    []
  );

  return (
    <>
      <div className="contents">
        <div className="page-title-wrap" data-type="responsive">
          <h2 className="h-tit">AI 영상 분석 점수 통계</h2>
        </div>
        <div className="tbl-wrap">
          <table className="tbl data row">
            <caption>조회 조건 선택 테이블입니다.</caption>
            <tbody>
              <tr>
                <th scope="row">조회 기간</th>
                <DateFilter
                  onDateChange={handleFilterChange}
                  customStartDate={customStartDate}
                  customEndDate={customEndDate}
                  setCustomStartDate={setCustomStartDate}
                  setCustomEndDate={setCustomEndDate}
                />
              </tr>
              <tr>
                <th scope="row">조회 구분</th>
                <td>
                  <div className="input-group">
                    <label className="hidden-label" htmlFor="sel1">
                      라벨
                    </label>
                    <select
                      id="sel1"
                      className="form-select md"
                      value={selectedApplySe}
                      onChange={e => setSelectedApplySe(e.target.value)}
                    >
                      <option value="">조사 구분</option>
                      <option value="A">정기</option>
                      <option value="O">수시</option>
                    </select>
                    <label className="hidden-label" htmlFor="sel2">
                      라벨
                    </label>
                    <select
                      id="sel2"
                      className="form-select md"
                      value={selectedScoreType}
                      onChange={handleScoreTypeChange}
                    >
                      <option value="OWAS">OWAS</option>
                      <option value="REBA">REBA</option>
                      <option value="RULA">RULA</option>
                    </select>
                  </div>
                </td>
              </tr>
              <tr>
                <th scope="row">조회 구분</th>
                <td>
                  <div className="input-group">
                    <label className="hidden-label" htmlFor="sel3">
                      라벨
                    </label>
                    <select
                      id="sel3"
                      className="form-select md"
                      value={selectedEntNm}
                      onChange={e => setSelectedEntNm(e.target.value)}
                    >
                      <option value="">기업 선택</option>
                      {aiEntNmList.map((item, index) => (
                        <option key={index} value={item.entNm}>
                          {item.entNm}
                        </option>
                      ))}
                    </select>
                    <label className="hidden-label" htmlFor="sel4">
                      라벨
                    </label>
                    <select
                      id="sel4"
                      className="form-select md"
                      value={selectedTpbizNm}
                      onChange={handleTpbizChange}
                    >
                      <option value="">업종 선택</option>
                      {aiTpbizNmList.map((item, index) => (
                        <option key={index} value={item.tpbizNm}>
                          {item.tpbizNm}
                        </option>
                      ))}
                    </select>
                    <label className="hidden-label" htmlFor="sel5">
                      라벨
                    </label>
                    <select
                      id="sel5"
                      className="form-select md"
                      value={selectedTaskNm}
                      onChange={e => setSelectedTaskNm(e.target.value)}
                      disabled={!selectedTpbizNm}
                    >
                      <option value="">공정 선택</option>
                      {filteredTaskList.map((item, index) => (
                        <option key={index} value={item.taskNm}>
                          {item.taskNm}
                        </option>
                      ))}
                    </select>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="page-btn-wrap gap-lg">
          <button
            type="button"
            className="btn xlg primary"
            onClick={handleSearch}
          >
            검색하기
          </button>
        </div>
        <div className="tbl-wrap">
          <div className="txt-box bg-white">
            {chartData.categories && chartData.categories.length > 0 ? (
              <CustomChart type="bar" data={chartData} options={chartOptions} />
            ) : (
              <p>데이터를 불러오는 중이거나 데이터가 없습니다.</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const DateFilter = ({
  onDateChange,
  customStartDate,
  customEndDate,
  setCustomStartDate,
  setCustomEndDate,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState('전체');
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const { modalService } = useContext(ModalContext);
  const [displayStartDate, setDisplayStartDate] = useState('');
  const [displayEndDate, setDisplayEndDate] = useState('');

  const formatDateForDisplay = date => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const validateDates = (date, comparisonDate, type) => {
    if (type === 'endDate' && !comparisonDate) {
      modalService.errorAlert('먼저 시작 날짜를 선택하세요.');
      return false;
    }

    const selectedDate = new Date(date);
    const compareDate = comparisonDate ? new Date(comparisonDate) : null;

    if (type === 'startDate' && compareDate && selectedDate > compareDate) {
      modalService.errorAlert('시작 날짜는 종료 날짜보다 이후일 수 없습니다.');
      return false;
    }

    if (type === 'endDate' && compareDate && selectedDate < compareDate) {
      modalService.errorAlert('종료 날짜는 시작 날짜보다 이전일 수 없습니다.');
      return false;
    }

    return true;
  };

  const handleStartDateChange = date => {
    const formattedDate = formatDateToISOString(date);
    const displayDate = formatDateForDisplay(date);
    if (!validateDates(formattedDate, customEndDate, 'startDate')) {
      return;
    }
    setCustomStartDate(formattedDate);
    onDateChange({ startDate: formattedDate, endDate: customEndDate });
    setDisplayStartDate(displayDate);
  };

  const handleEndDateChange = date => {
    const formattedDate = formatDateToISOString(date);
    const displayDate = formatDateForDisplay(date);
    if (!validateDates(formattedDate, customStartDate, 'endDate')) {
      return;
    }
    setCustomEndDate(formattedDate);
    onDateChange({ startDate: customStartDate, endDate: formattedDate });
    setDisplayEndDate(displayDate);
  };

  const handlePeriodSelect = label => {
    setSelectedPeriod(label);

    if (label !== '기간 조회') {
      const today = new Date();
      let startDate = null;
      let endDate = new Date();

      if (label === '3개월') {
        startDate = new Date(today.setMonth(today.getMonth() - 3));
      } else if (label === '6개월') {
        startDate = new Date(today.setMonth(today.getMonth() - 6));
      } else if (label === '1년') {
        startDate = new Date(today.setFullYear(today.getFullYear() - 1));
      } else if (label === '2년') {
        startDate = new Date(today.setFullYear(today.getFullYear() - 2));
      } else if (label === '3년') {
        startDate = new Date(today.setFullYear(today.getFullYear() - 3));
      } else if (label === '전체') {
        startDate = null;
        endDate = null;
      }

      const formattedStartDate = formatDateToISOString(startDate);
      const formattedEndDate = formatDateToISOString(endDate, true);

      onDateChange({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    }
  };

  return (
    <td>
      <div className="input-group">
        {['전체', '3개월', '6개월', '1년', '2년', '3년', '기간 조회'].map(
          (label, idx) => (
            <div className="form-chip sm" key={idx}>
              <input
                type="radio"
                className="radio"
                name="rdo_chip_period"
                id={`rdo_chip_period_${idx}`}
                checked={selectedPeriod === label}
                onChange={() => handlePeriodSelect(label)}
              />
              <label
                className="form-chip-outline"
                htmlFor={`rdo_chip_period_${idx}`}
                style={{ height: 'auto' }}
              >
                {label}
              </label>
            </div>
          )
        )}
      </div>
      {selectedPeriod === '기간 조회' && (
        <div className="input-group gap-md">
          <DatePicker
            selectedDate={displayStartDate}
            onDateChange={handleStartDateChange}
            label="시작 날짜"
          />
          <DatePicker
            selectedDate={displayEndDate}
            onDateChange={handleEndDateChange}
            label="종료 날짜"
          />
        </div>
      )}
    </td>
  );
};

export default ResScoreStats;
